import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";


export interface PageContext {
  isPageWithHeader: boolean;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  buttonText?: string;
  showSync?: boolean;
  hideHeaderOnMobile?: boolean;
}

export const usePageContext = (): PageContext => {
  const location = useLocation();

  return useMemo(() => {
    const path = location.pathname;

    // Dashboard has its own layout - no page header needed
    if (path === "/" || path === "/dashboard") {
      return { isPageWithHeader: false };
    }

    // Calendar page
    if (path === "/calendar") {
      return {
        isPageWithHeader: true,
        title: "Calendar",
        subtitle: "Manage events and important dates",
        icon: (
          <Box
            component="img"
            src="/icons/calender.webp"
            alt="Calendar"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "ADD EVENT",
      };
    }

    // User Home — page supplies its own hero
    if (
      path.startsWith("/user-management/") &&
      path !== "/user-management"
    ) {
      return { isPageWithHeader: false };
    }

    // User Management
    if (path === "/user-management" || path === "/manage-users") {
      return {
        isPageWithHeader: true,
        title: "Users",
        subtitle: "Manage users and invitations for your organization",
        icon: (
          <Box
            component="img"
            src="/icons/dashboard.webp"
            alt="Users"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "ADD USER",
      };
    }

    // Team Management
    if (path === "/team-management") {
      const certifications =
        new URLSearchParams(location.search).get("tab") === "certifications";
      return {
        isPageWithHeader: true,
        title: "Team",
        subtitle: certifications
          ? "See which staff have completed a certification"
          : "Manage team members and permissions for your organization",
        buttonText: certifications ? undefined : "ADD MEMBER",
        icon: (
          <Box
            component="img"
            src="/icons/secure.webp"
            alt="Team"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
      };
    }

    // Circles
    if (path === "/circles") {
      return {
        isPageWithHeader: true,
        title: "Circles",
        subtitle:
          "Create and manage communication circles for your organization",
        icon: (
          <Box
            component="img"
            src="/icons/circles.png"
            alt="Circles"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "CREATE CIRCLE",
      };
    }

    // Notice Feed
    if (path === "/view-notices") {
      return {
        isPageWithHeader: true,
        title: "Notices",
        subtitle: "View and manage all notices for your organization",
        icon: (
          <Box
            component="img"
            src="/icons/smart.webp"
            alt="Notices"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "CREATE NOTICE",
      };
    }

    // Drive
    if (path === "/drive") {
      return {
        isPageWithHeader: true,
        title: "Drive",
        subtitle: "Access all files and attachments from your circles",
        icon: (
          <Box
            component="img"
            src="/icons/cloud.webp"
            alt="Drive"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "UPLOAD FOLDER",
      };
    }

    // Library hub + church resources — page supplies its own hero
    if (path === "/library") {
      return { isPageWithHeader: false };
    }
    if (path === "/resources" || path.startsWith("/resources/")) {
      return { isPageWithHeader: false };
    }

    // Ad Center
    if (path === "/ad-center") {
      return {
        isPageWithHeader: true,
        title: "Ad Center",
        subtitle: "View and manage all advertisements for your organization",
        icon: (
          <Box
            component="img"
            src="/icons/bookademo.webp"
            alt="Ad Center"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        buttonText: "CREATE ADVERTISEMENT",
      };
    }

    // Profile
    if (path === "/profile") {
      return {
        isPageWithHeader: true,
        title: "Account",
        subtitle: "Manage your profile and account settings",
        icon: (
          <Box
            component="img"
            src="/icons/profile.webp"
            alt="Account"
            sx={{
              width: { xs: 60, md: 70 },
              height: { xs: 60, md: 70 },
              objectFit: "contain",
            }}
          />
        ),
        showSync: false,
        hideHeaderOnMobile: true,
      };
    }

    // Default - no page header
    return { isPageWithHeader: false };
  }, [location.pathname, location.search]);
};
